import React from "react";
import {
    Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, Stack, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import HomeIcon from "@mui/icons-material/Home";
import EmailIcon from "@mui/icons-material/Email";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import type { HistoryEntry } from "../../../../hooks/Clients/dashboard";
import { useLanguage } from "../../../../context/LanguageContext";

interface Props {
    open: boolean;
    onClose: () => void;
    history: HistoryEntry[];
    isLoading: boolean;
    error: string;
}

// API always returns French type labels — used as stable keys for icon lookup and translation mapping
const TYPE_ICONS: Record<string, React.ReactNode> = {
    "Changement d'adresse":          <HomeIcon fontSize="small" />,
    "Changement d'e-mail":           <EmailIcon fontSize="small" />,
    "Changement de téléphone mobile":<SmartphoneIcon fontSize="small" />,
    "Changement de téléphone fixe":  <PhoneIcon fontSize="small" />,
    "Changement de données":         <PersonIcon fontSize="small" />,
};

const TYPE_KEYS: Record<string, string> = {
    "Changement d'adresse":          "pages.customers.dashboard.history.changeAddress",
    "Changement d'e-mail":           "pages.customers.dashboard.history.changeEmail",
    "Changement de téléphone mobile":"pages.customers.dashboard.history.changeMobile",
    "Changement de téléphone fixe":  "pages.customers.dashboard.history.changeLandline",
    "Changement de données":         "pages.customers.dashboard.history.changeData",
};

const formatHistoryDate = (iso: string): string => {
    // "YYYY-MM-DD HH:MI:SS" → "DD/MM/YYYY HH:MI:SS"
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2}) (.+)$/);
    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}` : iso;
};

const PersonHistoryDialog = ({ open, onClose, history, isLoading, error }: Props) => {
    const { t } = useLanguage();
    // UPDATE_DATE au format "YYYY-MM-DD HH:MI:SS", donc triable directement par ordre lexicographique
    const sortedHistory = [...history].sort((a, b) => b.UPDATE_DATE.localeCompare(a.UPDATE_DATE));

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
                <HistoryIcon color="primary" fontSize="small" />
                {t("pages.customers.dashboard.history.title")}
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={32} />
                    </Box>
                )}
                {error && (
                    <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                )}
                {!isLoading && !error && history.length === 0 && (
                    <Typography color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
                        {t("pages.customers.dashboard.history.noHistory")}
                    </Typography>
                )}
                {!isLoading && history.length > 0 && (
                    <Stack divider={<Divider />}>
                        {sortedHistory.map((entry, idx) => (
                            <Box key={idx} sx={{ px: 2.5, py: 1.75 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                    <Box sx={{ color: "primary.main", display: "flex" }}>
                                        {TYPE_ICONS[entry.TYPE] ?? <HistoryIcon fontSize="small" />}
                                    </Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {TYPE_KEYS[entry.TYPE] ? t(TYPE_KEYS[entry.TYPE]) : entry.TYPE}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {formatHistoryDate(entry.UPDATE_DATE)}
                                </Typography>
                                {entry.DETAIL && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                        {entry.DETAIL}
                                    </Typography>
                                )}
                                {entry.MOTIF && (
                                    <Chip
                                        label={`${t("pages.customers.dashboard.history.motif")} : ${entry.MOTIF}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PersonHistoryDialog;
